
import { Getter, Action } from 'vuex-class';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { cloneDeep, isEqual, isNil } from 'lodash';
import { CATERING_OPEN_ITEM } from '@/utils/constants';
import { PERMISSION } from '@/permissions';
import omitBy from 'lodash/omitBy';
import Toast from '@/utils/toast';
import ListItem from '@/components/shared/ListItem.vue';
import ModalWrapper from '@/components/shared/ModalWrapper.vue';
import MoveModal from '@/components/shared/MoveModal.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import ItemEditor from '@/components/item/ItemEditor.vue';

const namespace: string = 'items';

@Component<ItemList>({
	components: {
		ListItem,
		ModalWrapper,
		ItemEditor,
		StandardModal,
		MoveModal
	}
})
export default class ItemList extends Vue {
	@Action('fetchMenus', { namespace: 'menus' }) private fetchMenus!: () => Promise<Menu[]>;
	@Getter('getMenus', { namespace: 'menus' }) private menusData!: Menu[];
	@Action('deleteMenuItem', { namespace }) private deleteMenuItem!: (payload: object) => Promise<void>;
	@Action('deleteMenuSectionItem', { namespace }) private deleteMenuSectionItem!: (payload: object) => Promise<void>;
	@Action('duplicateMenuSectionItem', { namespace }) private duplicateMenuSectionItem!: (payload: object) => Promise<void>;
	@Action('duplicateMenuItem', { namespace }) private duplicateMenuItem!: (payload: object) => Promise<void>;
	@Action('setSelectedItem', { namespace }) private setSelectedItem!: (item: MenuItem | object) => Promise<void>;
	@Action('updateMenuSectionItem', { namespace }) private updateMenuSectionItem!: (payload: object) => Promise<void>;
	@Action('updateMenuItem', { namespace }) private updateMenuItem!: (payload: object) => Promise<void>;
	@Getter('getSelectedItem', { namespace }) private selectedItem!: MenuItem;
	@Getter('getFetchMenusComplete', { namespace: 'menus' }) private fetchMenusComplete!: boolean;
	@Getter('isPosIntegrated', { namespace: 'auth' }) private isPosIntegrated!: boolean;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Prop({ type: Object, default: () => {} }) private menu!: Menu;
	@Prop({ type: Object, default: () => {} }) private section!: MenuSection;

	private PERMISSION = PERMISSION;
	private showDialog: boolean = false;
	private showConfirmCloseModal: boolean = false;
	private isCreating: boolean = false;
	private showDeleteModal: boolean = false;
	private hasBeenEdited: boolean = false;
	private loading: boolean = false;
	private showMoveModal: boolean = false;

	private itemToDelete: MenuItem = {} as MenuItem;
	private moveItem: MenuItem = {} as MenuItem;
	private selectedLocale: RestaurantLocale|null = null;

	private selectedItemBeforeEditSnapshot: MenuItem = {} as MenuItem;

	private actionsArray: ActionItem[] = [
		{
			iconName: 'mdi-archive-arrow-down',
			buttonLabel: this.$t('shared.actions_array.btn_archive'),
			onClick: async (evt: object, item: MenuItem): Promise<void> => {
				this.archiveItem(item);
			},
			permission: PERMISSION.ITEM_ARCHIVE
		},
		{
			iconName: 'mdi-archive-arrow-up',
			buttonLabel: this.$t('shared.actions_array.btn_publish'),
			onClick: async (evt: object, item: MenuItem): Promise<void> => {
			 	this.publishItem(item);
			},
			permission: PERMISSION.ITEM_ARCHIVE,
			disabled: this.menu?.name === CATERING_OPEN_ITEM.MENU_NAME
		},
		{
			iconName: 'mdi-pencil',
			buttonLabel: this.$t('shared.actions_array.btn_edit'),
			onClick: (evt: object, item: MenuItem): void => {
				this.openEditor(false, item);
				this.setSelectedItem(item);
			}
		},
		{
			iconName: 'mdi-file-multiple',
			buttonLabel: this.$t('shared.actions_array.btn_duplicate'),
			onClick: async (evt: object, item: MenuItem): Promise<void> => {
				this.duplicateItem(item);
			},
			permission: PERMISSION.ITEM_DUPLICATE
		},
		{
			iconName: 'mdi-arrow-right',
			buttonLabel: this.$t('shared.actions_array.btn_move'),
			onClick: (evt: object, item: MenuItem): void => {
				this.openMoveModal(item);
			},
			permission: PERMISSION.ITEM_MOVE
		},
		{
			iconName: 'mdi-delete',
			buttonLabel: this.$t('shared.actions_array.btn_delete'),
			onClick: (evt: object, item: MenuItem): void => {
				this.setItemToDeleteName(item);
			},
			permission: PERMISSION.ITEM_DELETE
		},
		{
			iconName: 'mdi-window-close',
			buttonLabel: this.$t('shared.actions_array.btn_cancel'),
			onClick: null
		}
	];

	private get type (): string {
		return this.section ? 'sectionItem' : 'menuItem';
	}

	private get getAvailableMoveDestinations(): Menu[] {
		return this.menusData.filter((_menu) => (
			this.type === 'sectionItem'
				? _menu.allow_sections && _menu.sections?.length
				: !_menu.allow_sections && _menu.id !== this.menu.id
		));
	}

	private get items(): MenuItem[] {
		if (this.section?.items) {
			return this.section.items;
		}
		else {
			return (this.menu?.items) || [];
		}
	}

	private get publishedItems(): MenuItem[] {
		return this.items
			.filter(item => item.published_at)
			.map(item => ({ ...item, menu_id: this.menu.id as number }));
	}

	private get unpublishedItems(): MenuItem[] {
		return this.items.filter(item => !item.published_at);
	}

	private async closeMoveModal (saved: boolean = false) {
		this.showMoveModal = false;
		if (saved) {
			this.loading = true;
			// The move action changes multiple sections across different menus, which is why
			// we need to refetch the menus, not just the sections.
			await this.fetchMenus()
				.catch((errorMessage) => {
					Toast.error(this.$t('menu.item.error_fetching_items', { errorMessage }));
				});
			this.loading = false;
		}
	}

	private async openMoveModal (item: MenuItem) {
		this.showMoveModal = true;
		this.moveItem = item;
	}

	/**
	 * Open Item Modal
	 * Called by EditMenus.vue to open the item editor
	 *
	 * @param {boolean} creating
	 * @param {MenuItem} [item]
	 * @return {void}
	 */
	public openEditor(creating: boolean, item?: MenuItem) {
		this.showDialog = true;
		this.isCreating = creating;

		if (!this.isCreating && item) {
			// Gotta validate a few things for isEqual checker
			this.selectedItemBeforeEditSnapshot = cloneDeep(item);
			if(this.selectedItemBeforeEditSnapshot.ingredients === '') {
				delete (this.selectedItemBeforeEditSnapshot as any).ingredients;
			}
			if(this.selectedItemBeforeEditSnapshot.price_unit === '') {
				delete (this.selectedItemBeforeEditSnapshot as any).price_unit;
			}
			if(!this.selectedItemBeforeEditSnapshot.additional_prices) {
				this.selectedItemBeforeEditSnapshot.additional_prices = { A: this.selectedItemBeforeEditSnapshot.price };
			}
		}
		else {
			this.selectedItemBeforeEditSnapshot = { price_type: this.$t('menu.item.editor.default_price.type_default_value') } as MenuItem;
		}


		document.documentElement.classList.add('modal-open');
	}

	/**
	 * Cancel closing of modal through the confirmation close modal
	 *
	 * @return {void}
	 */
	private cancelCloseModal(): void {
		this.showConfirmCloseModal = false;
	}

	/**
	 * Set the item name for the deletion modal
	 *
	 * @return {void}
	 */
	private setItemToDeleteName(item: MenuItem): void {
		this.showDeleteModal = true;
		this.itemToDelete = item;
	}

	/**
	 * Check if needs to show the confirm close modal to the user
	 *
	 * @return {void}
	 */
	private confirmClose(saved: boolean = false) {
		const selectedItemToCompare = omitBy(this.selectedItem, isNil);
		const selectedItemBeforeEditSnapshotToCompare = omitBy(this.selectedItemBeforeEditSnapshot, isNil);
		this.selectedLocale = null;
		if (saved || (!this.hasBeenEdited && isEqual(selectedItemToCompare, selectedItemBeforeEditSnapshotToCompare))) {
			this.close();
		}
		else {
			this.showConfirmCloseModal = true;
		}
	}

	/**
	 * Close the ItemEditor modal
	 *
	 * @return {void}
	 */
	private close() {
		this.showConfirmCloseModal = false;
		this.hasBeenEdited = false;
		this.showDialog = false;
		this.isCreating = false;

		// Remove the selected item from the store
		this.setSelectedItem({});
		document.documentElement.classList.remove('modal-open');
	}

	private cancel(): void {
		this.showDeleteModal = false;
		this.itemToDelete = {} as MenuItem;
	}


	private async archiveItem(item: MenuItem): Promise<void> {
		if (this.section) {
			await this.updateMenuSectionItem({ menu: this.menu, section: this.section, item: { id: item.id, published: false } })
				.catch((errorMessage: string) => {
					Toast.error(this.$t('shared.error_archiving', { name: item.name, errorMessage }));
				});
		}
		else {
			await this.updateMenuItem({ menu: this.menu, item: { id: item.id, published: false } })
				.catch((errorMessage: string) => {
					Toast.error(this.$t('shared.error_archiving', { name: item.name, errorMessage }));
				});
		}
	}

	private async publishItem(item: MenuItem): Promise<void> {
		if (this.section) {
			await this.updateMenuSectionItem({ menu: this.menu, section: this.section, item: { id: item.id, published: true } })
				.catch((errorMessage: string) => {
					Toast.error(this.$t('shared.error_publishing', { name: item.name, errorMessage }));
				});
		}
		else {
			await this.updateMenuItem({ menu: this.menu, item: { id: item.id, published: true } })
				.catch((errorMessage: string) => {
					Toast.error(this.$t('shared.error_publishing', { name: item.name, errorMessage }));
				});
		}
	}

	private async duplicateItem(item: MenuItem): Promise<void>{
		this.$emit('duplicating', true);
		if (this.section) {
			await this.duplicateMenuSectionItem({ oldMenu: this.menu, newMenu: this.menu, oldSection: this.section, newSection: this.section, item })
				.catch((errorMessage: string) => {
					Toast.error(this.$t('shared.error_duplicating', { name: item.name, errorMessage }));
				})
				.finally(() => {
					this.$emit('duplicating', false);
				});
		}
		else {
			await this.duplicateMenuItem({ oldMenu: this.menu, newMenu: this.menu, item })
				.catch((errorMessage: string) => {
					Toast.error(this.$t('shared.error_duplicating', { name: item.name, errorMessage }));
				})
				.finally(() => {
					this.$emit('duplicating', false);
				});
		}
	}

	/**
	 * User confirmed deletion of the item, so we delete it
	 * and close the modal
	 *
	 * @return {Promise<void>}
	 */
	private async confirm(): Promise<void> {
		this.loading = true;
		if (this.section) {
			await this.deleteMenuSectionItem({ menu: this.menu, section: this.section, item: this.itemToDelete })
				.catch((errorMessage) => {
					Toast.error(this.$t('shared.error_deleting', { name: this.itemToDelete.name, errorMessage }));
				});
		}
		else {
			await this.deleteMenuItem({ menu: this.menu, item: this.itemToDelete })
				.catch((errorMessage) => {
					Toast.error(this.$t('shared.error_deleting', { name: this.itemToDelete.name, errorMessage }));
				});
		}
		this.itemToDelete = {} as MenuItem;
		this.loading = false;
		this.showDeleteModal = false;
	}
}
